import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Building2, Trees, Wrench, Eye, ClipboardList, Car, Ship, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import FaqSection from '../components/FaqSection';

const BendinatPage = () => {
  const { language, t } = useLanguage();
  const content = t.bendinatPage;

  const services = [
    {
      icon: Building2,
      title: t.servicesTitles.propertyManagement,
      desc: language === 'EN' 
        ? 'Overall management and coordination of your Bendinat property while you are away.'
        : 'Gestión y coordinación general de su propiedad en Bendinat mientras está fuera.'
    },
    {
      icon: Trees,
      title: t.servicesTitles.gardenPool,
      desc: language === 'EN'
        ? 'Professional coordination of garden and pool maintenance through trusted local specialists.'
        : 'Coordinación profesional del mantenimiento de jardín y piscina con especialistas locales de confianza.'
    },
    {
      icon: Wrench,
      title: t.servicesTitles.renovations,
      desc: language === 'EN'
        ? 'Expert coordination of renovation projects with on-site supervision and quality control.'
        : 'Coordinación experta de proyectos de renovación con supervisión in situ y control de calidad.'
    },
    {
      icon: Eye,
      title: t.servicesTitles.propertySupervision,
      desc: language === 'EN'
        ? 'Regular property visits with detailed condition reports and photo documentation.'
        : 'Visitas regulares a la propiedad con informes detallados y documentación fotográfica.'
    },
    {
      icon: ClipboardList,
      title: t.servicesTitles.maintenanceCoordination,
      desc: language === 'EN'
        ? 'Coordination of all technical maintenance from plumbing to electrical systems.'
        : 'Coordinación de todo el mantenimiento técnico desde fontanería hasta sistemas eléctricos.'
    },
    {
      icon: Car,
      title: t.servicesTitles.vehicle,
      desc: language === 'EN'
        ? 'Vehicle maintenance coordination including battery care and regular checks.'
        : 'Coordinación del mantenimiento de vehículos incluyendo cuidado de batería y revisiones.'
    },
  ];

  return (
    <div data-testid="bendinat-page">
      <Helmet>
        <title>Property Management Bendinat | Balearic Home Solutions</title>
        <meta name="description" content="Private property management in Bendinat, Mallorca. Local oversight, maintenance coordination and transparent reporting for international homeowners. Based in Calvià." />
        <link rel="canonical" href="https://balearichomesolutions.com/bendinat" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={14} className="text-stone-500" />
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500">
                {content.hero.location}
              </p>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-navy tracking-tight mb-6">
              {content.hero.title}
            </h1>
            <p className="text-stone-600 text-lg md:text-xl leading-relaxed max-w-3xl">
              {content.hero.subtitle}
            </p>
            <div className="mt-10">
              <Link
                to="/contact"
                data-testid="bendinat-cta-primary"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                {content.hero.cta}
                <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Bendinat Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
                {content.whyBendinat.label}
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-6">
                {content.whyBendinat.title}
              </h2>
              <div className="space-y-4 text-stone-600 leading-relaxed">
                <p>{content.whyBendinat.text1}</p>
                <p>{content.whyBendinat.text2}</p>
                <p className="text-navy font-medium">{content.whyBendinat.text3}</p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1200&q=80"
                alt="Luxury villa in Bendinat, Mallorca"
                className="w-full h-auto aspect-[4/3] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-navy/80 to-transparent p-6">
                <p className="text-white/90 text-sm font-medium">Bendinat, Mallorca</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
              {content.services.label}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl text-navy tracking-tight mb-4">
              {content.services.title}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              {content.services.description}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 hover:shadow-lg transition-shadow duration-300"
              >
                <service.icon size={32} className="text-navy mb-4" />
                <h3 className="font-heading text-xl text-navy mb-3">{service.title}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              className="text-navy font-medium text-sm uppercase tracking-wider hover:underline underline-offset-4 inline-flex items-center gap-2"
            >
              {content.services.viewAll}
              <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection content={content.faq} testId="bendinat-faq" />

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-navy">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-white tracking-tight mb-6">
              {content.cta.title}
            </h2>
            <p className="text-white/70 leading-relaxed mb-8">
              {content.cta.description}
            </p>
            <Link
              to="/contact"
              data-testid="bendinat-cta-bottom"
              className="bg-white text-navy px-8 py-4 text-xs font-medium uppercase tracking-widest hover:bg-stone-100 transition-all inline-flex items-center justify-center gap-2"
            >
              {content.cta.button}
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BendinatPage;
